import { Suspense, useMemo, useState } from "react"
import { Outlet } from "react-router-dom"
import Navbar from "../navbar"
import Header from "../header"
import { useAppSelector, usePendingEmbedDomains } from "../../hooks"
import {
  useCanUseAssistant,
  useIsBankAdmin,
  useIsSuperAdmin
} from "../../hooks/use-user-roles"
import { selectUser } from "../../../redux/slices/auth"
import { ALLOWED_ROLES_BY_ROUTE, NavbarItems } from "../../constants"
import { getUserRole } from "../../utils"
import ErrorBoundary from "../../utils/error-boundary"
import { MobileAppPopup } from "../../ui"
import PageLoader from "../../ui/page-loader"
import StaffAiDrawer from "../../ui/staff-ai-chat/drawer"
import { StaffChatSessionProvider } from "../../ui/staff-ai-chat/session-context"
import { useFindChannelsQuery } from "../../../redux/queries"

const FullLayout = () => {
  const user = useAppSelector(selectUser)
  const isSuperAdmin = useIsSuperAdmin()
  const isBankAdmin = useIsBankAdmin()
  const canUseAssistant = useCanUseAssistant()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  const toggleSidebarCollapsed = () => setSidebarCollapsed(!sidebarCollapsed)

  const orgId = user?.user?.org?.id ?? ""
  const { data: channelsForNav = [] } = useFindChannelsQuery(
    { org_id: orgId },
    { skip: !isBankAdmin || !orgId }
  )
  const hasActiveChannel = channelsForNav.some(c => c.status === "active")

  // Websites blocked from loading an embed for not being on the approved-URL
  // list. Badging Integrations is what turns "my test page is broken" into
  // something the customer can find and fix without emailing support.
  const { count: blockedDomainCount } = usePendingEmbedDomains({
    enabled: !!orgId && (isBankAdmin || isSuperAdmin)
  })

  const userRole = getUserRole(user?.user.roles ?? [])
  const navItems = useMemo(() => {
    const items = userRole
      ? NavbarItems[userRole].filter(({ key, hidden }) => {
          if (
            !userRole ||
            hidden ||
            !ALLOWED_ROLES_BY_ROUTE[key].includes(userRole)
          ) {
            return false
          }
          if (key === "channel-leads" && isBankAdmin && !isSuperAdmin) {
            return hasActiveChannel
          }
          return true
        })
      : []

    return items.map(item => {
      if (item.key === "dashboard" && !isSuperAdmin) {
        return { ...item, name: "Dashboard" }
      }
      if (item.key === "integrations" && blockedDomainCount > 0) {
        return { ...item, pill: String(blockedDomainCount) }
      }
      return item
    })
  }, [
    userRole,
    isSuperAdmin,
    isBankAdmin,
    hasActiveChannel,
    blockedDomainCount
  ])

  const content = (
    <div className="h-dvh">
      <Navbar
        items={navItems}
        sidebarOpen={sidebarOpen}
        sidebarCollapsed={sidebarCollapsed}
        setSidebarOpen={setSidebarOpen}
        toggleSidebarCollapsed={toggleSidebarCollapsed}
      />
      <div
        className={`${
          sidebarCollapsed ? "lg:pl-24" : "lg:pl-64"
        } h-full flex flex-col flex-1 transition-all bg-primary-100`}
      >
        <Header setSidebarOpen={setSidebarOpen} />
        <main className="bg-white flex flex-1 overflow-auto flex-col">
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
      <MobileAppPopup />
      {canUseAssistant && <StaffAiDrawer />}
    </div>
  )

  return canUseAssistant ? (
    <StaffChatSessionProvider>{content}</StaffChatSessionProvider>
  ) : (
    content
  )
}

export default FullLayout
